/**
 * VacationToggle — sticky vacation-mode switch + banner (EPIC-006 Phase 7,
 * Task 7.1; story 006-009 front end).
 *
 * `GET /sellers/me/vacation` seeds the state; flipping the switch calls
 * `PUT /sellers/me/vacation`. When ON, a sticky banner announces that all
 * products are hidden from the storefront, with the relative "since" time
 * rendered via `Intl.RelativeTimeFormat` (locale-aware).
 *
 * Tokens are `--g-*` only; copy is the `seller-dashboard` i18n namespace
 * (ru/ky/en). The switch is a ≥44px target with an accessible label and
 * `aria-checked`; the banner is `role="status"` / `aria-live="polite"`.
 */
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  registerSellerDashboardI18n,
  SELLER_DASHBOARD_NS,
  type VacationStatusResponse,
} from "@/features/seller-dashboard/api";
import {
  usePutVacation,
  useVacationStatus,
} from "@/features/seller-dashboard/hooks/useSellerProducts";

/**
 * Format an ISO-8601 instant as a locale-aware relative phrase
 * ("2 hours ago"). Picks the largest sensible unit. Pure + SSR-safe.
 */
export function formatSince(iso: string, locale: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return iso;
  const deltaSec = Math.round((then - Date.now()) / 1000); // negative = past
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const abs = Math.abs(deltaSec);
  const divisions: Array<[number, Intl.RelativeTimeFormatUnit]> = [
    [60, "second"],
    [3600, "minute"],
    [86400, "hour"],
    [604800, "day"],
    [2629800, "week"],
    [31557600, "month"],
    [Infinity, "year"],
  ];
  const unitDivisors: Record<string, number> = {
    second: 1,
    minute: 60,
    hour: 3600,
    day: 86400,
    week: 604800,
    month: 2629800,
    year: 31557600,
  };
  for (const [limit, unit] of divisions) {
    if (abs < limit) {
      const value = Math.round(deltaSec / unitDivisors[unit]!);
      return rtf.format(value, unit);
    }
  }
  return rtf.format(Math.round(deltaSec / 31557600), "year");
}

export function VacationToggle(): ReactElement {
  registerSellerDashboardI18n();
  const { t, i18n } = useTranslation(SELLER_DASHBOARD_NS);
  const { data } = useVacationStatus();
  const putVacation = usePutVacation();

  const status: VacationStatusResponse = data ?? {
    vacation_mode: false,
    since: null,
  };
  const isOn = status.vacation_mode;

  const handleToggle = (): void => {
    putVacation.mutate(!isOn, {
      onError: () => toast.error(t("vacation.updateFailed")),
    });
  };

  return (
    <div
      className="sticky top-0 z-10 bg-[var(--g-ground)] pb-2 pt-1"
      data-testid="vacation-toggle"
    >
      <div className="flex items-center justify-between gap-3 rounded-[var(--g-radius)] border border-border bg-card px-4 py-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">
            {t("vacation.label")}
          </p>
          <p className="truncate text-xs text-muted-foreground">
            {t("vacation.hint")}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={isOn}
          aria-label={isOn ? t("vacation.toggleOff") : t("vacation.toggleOn")}
          onClick={handleToggle}
          disabled={putVacation.isPending}
          data-testid="vacation-switch"
          className={[
            "relative inline-flex h-6 min-h-[44px] w-[44px] shrink-0 items-center",
            "rounded-full px-0.5 transition-colors",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            "focus-visible:ring-offset-2 focus-visible:ring-offset-background",
            isOn ? "bg-primary" : "bg-muted",
          ].join(" ")}
        >
          <span
            aria-hidden="true"
            className={[
              "inline-block size-5 rounded-full bg-[var(--g-on-primary)] shadow-sm transition-transform",
              isOn ? "translate-x-[20px]" : "translate-x-0",
            ].join(" ")}
          />
        </button>
      </div>

      {isOn ? (
        <div
          role="status"
          aria-live="polite"
          data-testid="vacation-banner"
          className={[
            "mt-2 flex items-start gap-2 rounded-[var(--g-radius)]",
            "border border-[var(--g-warn)]/40 bg-[var(--g-warn)]/10 px-4 py-3",
          ].join(" ")}
        >
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[var(--g-warn)]">
              {t("vacation.bannerTitle")}
            </p>
            {status.since ? (
              <p className="text-xs text-muted-foreground">
                {t("vacation.bannerSince", {
                  since: formatSince(status.since, i18n.language),
                })}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default VacationToggle;
